import { CircularProgress, Grid, Stack, StackProps } from "@mui/material";
import { GridProps } from "@mui/system";
import { Typography } from "@platform/design-system";

import { formatDate } from "../../../utils";
import { useBrokerSummary } from "../../../hooks/useBrokerSummary";
import { CarrierName } from "../constants";
import { PersistencyPayout } from "../types";

const toFieldTestId = (title: string): string =>
  `summary-value-${title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}`;

export const SummaryField: React.FC<{
  title: string;
  loading?: boolean;
  description?: string;
  layout?: "stack" | "grid";
}> = ({ title, description, loading = false, layout = "stack" }) => {
  const valueTestId = toFieldTestId(title);

  if (layout === "grid") {
    return (
      <>
        <Grid size={{ xs: 6 }}>
          <Typography variant="body-medium" noWrap>
            <strong>{title}</strong>
          </Typography>
        </Grid>
        <Grid size={{ xs: 6 }}>
          {loading ? (
            <CircularProgress size="1rem" />
          ) : (
            <Typography variant="body-medium" data-testid={valueTestId}>
              {description || "-"}
            </Typography>
          )}
        </Grid>
      </>
    );
  }

  return (
    <Stack spacing={0}>
      <Typography variant="body-medium" noWrap>
        <strong>{title}</strong>
      </Typography>
      {loading ? (
        <CircularProgress size="1rem" />
      ) : (
        <Typography variant="body-medium" data-testid={valueTestId}>
          {description || "-"}
        </Typography>
      )}
    </Stack>
  );
};

type BonusSummaryDescriptionProps = {
  carrierName: CarrierName;
  currentRuleId?: string;
  startOfYearPremiumInput?: number;
  layout?: "stack" | "grid";
  bonusYear?: number;
};

export const BonusSummaryDescription: React.FC<
  BonusSummaryDescriptionProps
> = ({
  carrierName,
  currentRuleId,
  startOfYearPremiumInput,
  bonusYear,
  layout = "stack",
}) => {
  const {
    newBusinessThresholds,
    newBusinessPayouts,
    persistencyPayouts,
    loading,
    bonusYearSettingsData,
  } = useBrokerSummary({
    carrierName,
    carrierRuleId: currentRuleId,
    startOfYearPremiumInput,
    bonusYear,
  });

  const Container = (layout === "grid" ? Grid : Stack) as React.ComponentType<
    GridProps | StackProps
  >;
  const containerProps =
    layout === "grid" ? { container: true, spacing: 0 } : { spacing: 2 };

  return (
    <Container {...containerProps}>
      <SummaryField
        title="Bonus Description"
        description={bonusYearSettingsData?.bonusDescription}
        loading={loading}
        layout={layout}
      />
      <SummaryField
        title="Bonus Year Start"
        description={formatDate(bonusYearSettingsData?.bonusYearStart, true)}
        loading={loading}
        layout={layout}
      />
      <SummaryField
        title="Bonus Year End"
        description={formatDate(bonusYearSettingsData?.bonusYearEnd, true)}
        loading={loading}
        layout={layout}
      />
      <SummaryField
        title="Persistency Thresholds"
        description={bonusYearSettingsData?.persistencyThresholds}
        loading={loading}
        layout={layout}
      />
      {typeof persistencyPayouts === "string" && (
        <SummaryField
          title="Persistency Payouts"
          description={persistencyPayouts}
          loading={loading}
          layout={layout}
        />
      )}
      {persistencyPayouts &&
        typeof persistencyPayouts === "object" &&
        Object.values(persistencyPayouts).map((payout: PersistencyPayout) => (
          <SummaryField
            key={`${payout.label}-${payout.value}`}
            title={payout.label}
            description={payout.value}
            loading={loading}
            layout={layout}
          />
        ))}
      {newBusinessThresholds && (
        <SummaryField
          title="New Business Thresholds"
          description={newBusinessThresholds}
          loading={loading}
          layout={layout}
        />
      )}
      <SummaryField
        title="New Business Payouts"
        description={newBusinessPayouts}
        loading={loading}
        layout={layout}
      />
    </Container>
  );
};
